import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Compass, Database, X, Loader2, DollarSign, Users, ShieldCheck, Trophy, GraduationCap, Scale, Briefcase } from "lucide-react";
import { useOnboarding } from "@/lib/onboarding";
import { TOUR_TRACKS, type TrackId } from "@/lib/tourTracks";
import { useToast } from "@/hooks/use-toast";
import { useOrg, type Persona } from "@/lib/orgContext";

const PERSONA_TIPS: Record<Persona, { icon: typeof DollarSign; title: string; body: string }> = {
  Finance: {
    icon: DollarSign,
    title: "Tip for Finance members",
    body: "Start with the contract value dashboard to see total spend, upcoming renewals, and per-coach commitments at a glance.",
  },
  HR: {
    icon: Users,
    title: "Tip for HR members",
    body: "Head to coach profiles to review team rosters and watch the expiry timeline so no contract lapses unnoticed.",
  },
  Legal: {
    icon: ShieldCheck,
    title: "Tip for Legal members",
    body: "Open the audit log and risk tracker first to monitor clause changes, signing activity, and outstanding compliance items.",
  },
  AD: {
    icon: Trophy,
    title: "Tip for Athletic Directors",
    body: "Use the contracts dashboard to track program-wide commitments, upcoming renewals, and bench-strength at a glance.",
  },
  SA: {
    icon: GraduationCap,
    title: "Tip for Student-Athlete admins",
    body: "Coach profiles and roster views help you keep eligibility, scholarship, and contract data in one place.",
  },
};

const PERSONA_ORDER: Persona[] = ["Finance", "HR", "Legal", "AD", "SA"];

export function WelcomePanel() {
  const { status, loading, dismissWelcome, loadSampleData, startTour } = useOnboarding();
  const { org } = useOrg();
  const { toast } = useToast();
  const [seeding, setSeeding] = useState(false);

  if (loading || !status) return null;
  if (status.welcomeDismissedAt) return null;
  if (status.totalCoaches > 0 && !status.hasSampleData) return null;
  if (status.tourCompletedAt && status.totalCoaches === 0) {
    // tour already completed but they cleared everything; only hide once explicitly dismissed
  }

  const isAdmin = org?.role === "admin";
  const assignedPersonas = org?.personas ?? [];
  const personaTips = PERSONA_ORDER
    .filter((p) => assignedPersonas.includes(p))
    .map((p) => ({ persona: p, ...PERSONA_TIPS[p] }));

  async function handleLoadSample() {
    setSeeding(true);
    const result = await loadSampleData();
    setSeeding(false);
    if (result.ok) {
      toast({ title: "Sample data loaded", description: "Explore 5 coaches, 8 contracts, insurance, and risk items." });
    } else {
      toast({ title: "Couldn't load sample data", description: result.error ?? "Try again in a moment.", variant: "destructive" });
    }
  }

  function handleTour(track: TrackId = "quick") {
    startTour(track);
  }

  return (
    <Card className="relative border-[#0B1834]/20 bg-gradient-to-br from-blue-50/60 via-white to-white shadow-sm">
      <button
        onClick={() => dismissWelcome()}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground rounded-md p-1 hover:bg-muted transition-colors"
        aria-label="Dismiss welcome panel"
        data-testid="button-dismiss-welcome"
      >
        <X className="h-4 w-4" />
      </button>
      <CardContent className="p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-11 h-11 rounded-xl bg-[#0B1834] items-center justify-center flex-shrink-0 shadow-md">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900">
              Welcome to Zindeal{org?.institutionName ? `, ${org.institutionName}` : ""} 
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              See the product in action in under a minute. Take a quick tour of the key
              areas, or load realistic sample data you can explore and clear at any time.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => handleTour("quick")}
                className="bg-[#0B1834] hover:bg-[#0A152E] text-white"
                data-testid="button-start-tour"
              >
                <Compass className="h-4 w-4 mr-2" />
                Take a quick tour
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleTour("legal")}
                data-testid="button-start-tour-legal"
                title={`${TOUR_TRACKS.legal.description} (~${TOUR_TRACKS.legal.estMinutes} min)`}
              >
                <Scale className="h-4 w-4 mr-2" />
                Legal tour
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleTour("finance")}
                data-testid="button-start-tour-finance"
                title={`${TOUR_TRACKS.finance.description} (~${TOUR_TRACKS.finance.estMinutes} min)`}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Finance / AD
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleTour("admin")}
                data-testid="button-start-tour-admin"
                title={`${TOUR_TRACKS.admin.description} (~${TOUR_TRACKS.admin.estMinutes} min)`}
              >
                <Briefcase className="h-4 w-4 mr-2" />
                Admin onboarding
              </Button>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleLoadSample}
                  disabled={seeding || status.hasSampleData}
                  data-testid="button-load-sample"
                >
                  {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                  {status.hasSampleData ? "Sample data loaded" : seeding ? "Loading…" : "Load sample data"}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => dismissWelcome()}
                className="text-muted-foreground"
                data-testid="button-skip-welcome"
              >
                Skip
              </Button>
            </div>

            {status.hasSampleData && (
              <p className="text-xs text-muted-foreground mt-3">
                Sample data is loaded — items are clearly tagged and can be removed from Settings.
              </p>
            )}

            {personaTips.length > 0 && (
              <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="persona-tips">
                {personaTips.map((tip) => {
                  const Icon = tip.icon;
                  return (
                    <div
                      key={tip.persona}
                      className="flex items-start gap-3 rounded-lg border border-[#0B1834]/15 bg-white/70 p-3"
                      data-testid={`persona-tip-${tip.persona.toLowerCase()}`}
                    >
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#0B1834]/10 text-[#0B1834]">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{tip.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{tip.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
